/**
 * LocalizationDrawer – bottom-sheet drawer for country/language selection.
 * Architecture: LocalizationDrawer (container) > DrawerOverlay + DrawerPanel
 * Trigger: element with [data-localization-drawer-trigger]
 */
class LocalizationDrawer extends HTMLElement {
  static DRAWER_STORAGE_KEY = 'localization-drawer-prefs';

  /** @type {(e: KeyboardEvent) => void} */
  #boundHandleKeydown;

  constructor() {
    super();
    this.overlay = null;
    this.panel = null;
    this.closeBtn = null;
    this.confirmBtn = null;
    this.countrySelect = null;
    this.languageSelect = null;
    this.#boundHandleKeydown = this.#handleKeydown.bind(this);
  }

  connectedCallback() {
    this.overlay = this.querySelector('[data-drawer-overlay]');
    this.panel = this.querySelector('[data-drawer-panel]');
    this.closeBtn = this.querySelector('[data-drawer-close]');
    this.confirmBtn = this.querySelector('[data-drawer-confirm]');
    this.countrySelect = this.querySelector('[data-country-select]');
    this.languageSelect = this.querySelector('[data-language-select]');

    if (!this.overlay || !this.panel) return;

    this.#setupTriggers();
    this.#setupOverlay();
    this.#setupCloseButton();
    this.#setupConfirm();
    this.#restoreFromStorage();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#boundHandleKeydown);
    document.querySelectorAll('[data-localization-drawer-trigger]').forEach((el) => {
      el.removeEventListener('click', this.#openHandler);
    });
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.#overlayClickHandler);
    }
  }

  #openHandler = () => this.open();
  #overlayClickHandler = () => this.close();

  #setupTriggers() {
    const triggers = document.querySelectorAll('[data-localization-drawer-trigger]');
    triggers.forEach((trigger) => {
      trigger.addEventListener('click', this.#openHandler);
    });
  }

  #setupOverlay() {
    this.overlay.addEventListener('click', this.#overlayClickHandler);
  }

  #setupCloseButton() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
  }

  #setupConfirm() {
    if (this.confirmBtn) {
      this.confirmBtn.addEventListener('click', () => this.#handleConfirm());
    }
  }

  #handleKeydown(e) {
    if (e.key === 'Escape' && this.isOpen()) {
      this.close();
    }
  }

  #handleConfirm() {
    const country = this.countrySelect?.value ?? '';
    const language = this.languageSelect?.value ?? '';
    try {
      localStorage.setItem(LocalizationDrawer.DRAWER_STORAGE_KEY, JSON.stringify({ country, language }));
    } catch (err) {
      console.warn('localization-drawer: localStorage unavailable', err);
    }
    this.close();
    this.dispatchEvent(new CustomEvent('localization:confirm', { detail: { country, language }, bubbles: true }));
  }

  #restoreFromStorage() {
    try {
      const raw = localStorage.getItem(LocalizationDrawer.DRAWER_STORAGE_KEY);
      if (!raw) return;
      const { country, language } = JSON.parse(raw);
      if (country && this.countrySelect) this.countrySelect.value = country;
      if (language && this.languageSelect) this.languageSelect.value = language;
    } catch {
      // ignore
    }
  }

  isOpen() {
    return this.hasAttribute('open');
  }

  #setTriggersExpanded(expanded) {
    document.querySelectorAll('[data-localization-drawer-trigger]').forEach((el) => {
      el.setAttribute('aria-expanded', String(expanded));
    });
  }

  open() {
    this.setAttribute('open', '');
    this.#setTriggersExpanded(true);
    document.addEventListener('keydown', this.#boundHandleKeydown);
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.removeAttribute('open');
    this.#setTriggersExpanded(false);
    document.removeEventListener('keydown', this.#boundHandleKeydown);
    document.body.style.overflow = '';
  }

  toggle() {
    this.isOpen() ? this.close() : this.open();
  }
}

if (!customElements.get('localization-drawer')) {
  customElements.define('localization-drawer', LocalizationDrawer);
}
